import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GenericResModel } from 'countable@model';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ChecklistDuplicateTemplateModel, GetChecklistReqModel } from '../model/checklist/checklist-duplicate-template.model';
import { FirmChecklistSaveTemplateModel } from '../model/checklist/firm-checklist-save-template.model';
import { FirmSaveTemplateResModel } from '../model/checklist/firm-save-template-res.model';
import { FirmSaveTemplateModel } from '../model/checklist/firm-save-template.model';
import { CheckListTemplateFolderModel } from '../model/template/check-list-template-folder.model';
import { CheckListTemplateModel } from '../model/template/check-list-template.model';
import { ProcedureTemplateModel } from '../model/template/procedure-template.model';
import { TemplateFolderModel } from '../model/template/template-folder.model';
import { ApiService } from './api.service';
import { modules } from './app-config';
import { EncodingService } from './encoding.service';
import { RollForwardResponse, RollForwardRowData } from '../model/eng/roll-forward.model';

@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  addElements = new Subject<any>();
  fetchFirmReportData = new Subject<any>();
  openAddNotes: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  checkElements: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  userDetails: any;
  firmReportsData: any = [];
  defaultHeader: any = {};
  defaultFooter: any = {};
  HeadersFootersData: {};
  readonly CANADA_ELEMENTS = [
    'Entity Name',
    'Year End Date (CY)',
    'Practitioners Signature',
    'Date of Report',
    'City, Province',
    'Complete Address',
    'Street Address',
    'Balance Sheet',
    'Statement of Income and Retained Earnings',
    'Addressee - Management',
    'Addressee - Board of Directors',
    'Addressee - Shareholders',
    'Year End Date (PY)',
    'Note X',
    'Client City',
    'Client Province'
  ];
  readonly USA_ELEMENTS = [
    'Entity Name',
    'Year End Date (CY)',
    'Practitioners Signature',
    'Date of Report',
    'City, State',
    'Complete Address',
    'Street Address',
    'Balance Sheet',
    'Statement of Income and Retained Earnings',
    'Statement of Cashflow',
    'Statement of Changes in Stockholders Equity',
    'Addressee - Management',
    'Addressee - Board of Directors',
    'Addressee - Shareholders',
    'Year End Date (PY)',
    'Client City',
    'Client State'
  ];

  constructor(private http: HttpClient, private encodingService: EncodingService, private apiService: ApiService,
              private toaster: ToastrService) {
    this.userDetails = JSON.parse(localStorage.getItem('userDetails'));
  }

  saveTemplateFolder(folder: string, template: string): Observable<GenericResModel<number>> {
    return this.http.post<GenericResModel<number>>(environment.apiV1 + '/template/folder', {folder, template});
  }

  duplicateChecklistTemplate(input: ChecklistDuplicateTemplateModel): Observable<GenericResModel<any>> {
    return this.http.put<GenericResModel<any>>(environment.apiV1 + '/template/checklist-duplicate', input);
  }

  saveFirmChecklistTemplate(input: FirmChecklistSaveTemplateModel): Observable<GenericResModel<FirmChecklistSaveTemplateModel>> {
    return this.http.post<GenericResModel<FirmChecklistSaveTemplateModel>>(environment.apiV1 + '/template/firm-checklist', input);
  }

  fetchFirmChecklistTemplates(input: Partial<GetChecklistReqModel>): Observable<GenericResModel<string>> {
    return this.http.post<GenericResModel<string>>(environment.apiV1 + '/template/checklist', input);
  }

  saveFirmTemplate(input: FirmSaveTemplateModel[]): Observable<GenericResModel<FirmSaveTemplateResModel[]>> {
    return this.http.post<GenericResModel<FirmSaveTemplateResModel[]>>(environment.apiV1 + '/template/firm', input);
  }

  saveRfTemplate(param: string): Observable<GenericResModel<any>> {
    return this.http.post<GenericResModel<any>>(environment.apiV1 + '/template/rf', param);
  }

  getAllRfTemplate(clientFirmId: number, searchTxt: string): Observable<RollForwardRowData[]> {
    return this.http.get<GenericResModel<RollForwardRowData[]>>(environment.apiV1 + '/all-rf-template', {
      params: {
        'clientFirmId': clientFirmId,
        'searchTxt': searchTxt
      }
    }).pipe(map(res => {
      if (!res || res.status !== 200) {
        this.toaster.error('Could not load the template information');
      }
      return res?.data;
    }));
  }

  getRfTemplate(engId: number): Observable<RollForwardResponse> {
    return this.http.get<GenericResModel<RollForwardResponse>>(environment.apiV1 + '/template/rf', {params: {'engId': engId}})
      .pipe(map(res => {
        if (!res || res.status !== 200) {
          this.toaster.error('Could not load the template information');
        }
        return res?.data;
      }));
  }

  getCheckListFolderTemplateName(): Observable<string> {
    return this.http.get<GenericResModel<string>>(environment.apiV1 + '/checklist-folder-template-name').pipe(
      map(res => {
        if (res.status !== 200 && res.status !== 204) {
          res.errors.forEach(err => this.toaster.warning(err));
        }
        return res.data;
      })
    );
  }

  deleteRfTemplate(stringData: string): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(environment.apiV1 + '/rf-template-remove',
      {params: {'stringData': stringData}});
  }

  deleteCheckListTemplate(stringData: string): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(environment.apiV1 + '/check-list-template-remove',
      {params: {'stringData': stringData}});
  }

  renameTemplate(data: string): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(environment.apiV1 + '/template/rename', data);
  }

  renameTemplateFolder(templateFolderModel: TemplateFolderModel): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(environment.apiV1 + '/template/rename-folder', templateFolderModel);
  }

  moveTemplateFolder(checkListTemplateFolderModel: CheckListTemplateFolderModel): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(environment.apiV1 + '/template/move-check-list', checkListTemplateFolderModel);
  }

  deleteTemplateFolder(templateFolderId: number): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(environment.apiV1 + '/template/folder',
      {params: {'templateFolderId': templateFolderId}});
  }

  deleteTemplate(data: string): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(environment.apiV1 + '/templates', {params: {'data': data}});
  }

  renameCheckListTemplate(checkListTemplateModel: CheckListTemplateModel): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(environment.apiV1 + '/template/rename-check-list', checkListTemplateModel);
  }

  saveDefaultLetterTemplate(templateId: number): Observable<string> {
    return this.http.post<GenericResModel<string>>(environment.apiV1 + '/default-letter-template', templateId).pipe(
      map(res => {
        if (res.status !== 200 && res.status !== 204) {
          res.errors.forEach(err => this.toaster.warning(err));
        }
        return res.data;
      })
    );
  }

  getMasterCheckListLeftSide(searchIdx: string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(environment.apiV1 + '/master-check-list-left-side', {params: {'searchIdx': searchIdx}});
  }

  getReviewMasterFirm(isMaster: number | null | undefined, searchIdx: string | null | undefined): Observable<string> {
    let params = new HttpParams();
    if (isMaster !== null && isMaster !== undefined) {
      params = params.set('isMaster', isMaster.toString());
    }
    if (searchIdx) {
      params = params.set('searchIdx', searchIdx);
    }
    return this.http.get<GenericResModel<string>>(environment.apiV1 + '/review-master-firm', {params})
      .pipe(
        map(res => {
          if (res.status !== 200 && res.status !== 204) {
            res.errors.forEach(err => this.toaster.warning(err));
          }
          return res.data;
        })
      );
  }

  updateProcedureTemplate(procedureTemplateModel: ProcedureTemplateModel): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(environment.apiV1 + '/procedure-template', procedureTemplateModel);
  }

  deleteSubQuestions(data: string): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(environment.apiV1 + '/sub-question', {params: {'data': data}});
  }

  // Saves the reports given to the firm template json in S3
  saveToReportFirmTemplate(reportsToSave, saveType, reportName?): Promise<any> {
    // Prepares input for API
    let jsonString = JSON.stringify(reportsToSave);
    var blob = new Blob([jsonString], {type: 'text/json;charset=utf-8'});
    let formData: FormData = new FormData();
    const userData = JSON.parse(localStorage.getItem('userDetails'));
    let jsonFileName = userData.firmUserAcctId + '_FirmTemplate.json';
    const uploadObj = JSON.stringify({
      'module': modules[22],
      'firmId': userData.firmUserAcctId,
      'status': 0,
      'appSyncId': userData.useracctid,
      'extension': 'comp'
    });
    formData.append('upload', this.encodingService.enData(uploadObj, 'body'));
    formData.append('file', blob, jsonFileName);

    // Calls API to save in S3

    return new Promise((resolve, reject) => {
      this.apiService.saveAndGetFileNamePath(formData).subscribe(response => {
        if (response) {
          // Displays toaster message based on activity type
          if (saveType == 'Copy') {
            this.toaster.success(reportName + ' copied successfully to firm templates');
          } else if (saveType == 'Copy Multiple') {
            this.toaster.success('Reports copied successfully to firm templates');
          } else if (saveType == 'Default') {
            this.toaster.success('Default template updated successfully');
          } else if (saveType == 'Duplicate') {
            this.toaster.success('Report duplicated successfully');
          } else if (saveType == 'Delete Folder') {
            this.toaster.success('Folder deleted successfully');
          } else if (saveType == 'Delete') {
            this.toaster.success('Report deleted successfully');
          } else if (saveType == 'Move') {
            this.toaster.success('Report moved successfully');
          } else if (saveType == 'save') {
            this.toaster.success('Report saved successfully');
          } else if (saveType == 'Create') {
            this.toaster.success('Report template created successfully');
          } else if (saveType == 'Updated') {
            this.toaster.success('New Report updated successfully');
          } else if (saveType == 'Rename Folder') {
            this.toaster.success('Folder renamed successfully');
          } else if (saveType == 'Rename') {
            this.toaster.success('Report renamed successfully');
          }
        }
        resolve(true);
      }, error => {
        reject();
      });
    });
  }

  replaceKeysWithString(string: string, object: { [key: string]: any }): string {
    const keys = Object.keys(object);
    const escapedKeys = keys.map((key) => key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const re = new RegExp(escapedKeys.join('|'), 'g');
    string = string.replace(re, match => object[match]);
    return string;
  }

  replaceReportElements(obj: { [key: string]: any }, arr: any[]): any[] {
    arr.forEach(element => {
      if (element && element.content.length && obj) {
        element.content = this.replaceKeysWithString(element.content, obj);
      }
    });
    return arr;
  }

  // replace [] and span tags in/from editor
  replaceReportForEdit(array, type, compilationReportEditString?) {
    if (type == '[-span') {
      array.forEach(str => str.content = str.content.replace(/\[|\]/g, match => match === '[' ? '<span class=\'elementTag is-locked\' style=\'background-color: #DDF0DF;\'>' : '</span>'));
      return [...array];
    }
    if (type == '{-span') {
      if (compilationReportEditString) {
        return compilationReportEditString.replace(/\{|\}/g, match => match === '{' ? '<span class=\'elementTag is-locked\' style=\'background-color: #DDF0DF;\'>' : '</span>');
      } else {
        array.forEach(str => str.content = str.content.replace(/\{|\}/g, match => match === '{' ? '<span class=\'elementTag is-locked\' style=\'background-color: #DDF0DF;\'>' : '</span>'));
        return [...array];
      }
    }
    if (type == 'span-values') {
      return compilationReportEditString.replace(/<span[^>]*>([^<]*)<\/span>/g, '[$1]');
    }
    if (type == 'span-[') {
      array.forEach(input => input.content = input.content.replace(/<span[^>]*>([^<]*)<\/span>/g, '[$1]'));
      return [...array];
    }
    if (type == 'span-{') {
      if (compilationReportEditString) {
        return compilationReportEditString.replace(/<span class=[^>]*>([^<]*)<\/span>/g, '{$1}');
      } else {
        array.forEach(input => input.content = input.content.replace(/<span class=[^>]*>([^<]*)<\/span>/g, '{$1}'));
        return [...array];
      }
    }
    if (type == '[-{') {
      return compilationReportEditString.replace(/\[|\]/g, match => match === '[' ? '{' : '}');

    }
    if (type == '{-[') {
      return compilationReportEditString.replace(/\{|\}/g, match => match === '{' ? '[' : ']');
    }
  }

  // Fetches the firm template saved in S3 bucket
  getReportFirmTemplate(): Promise<any> {
    const userData = JSON.parse(localStorage.getItem('userDetails'));
    let jsonFileName = userData.firmUserAcctId + '_FirmTemplate.json';
    const uploadObj = {
      'module': modules[22],
      'firmId': userData.firmUserAcctId,
      'status': 0,
      'folderName': jsonFileName,
      'appSyncId': userData.useracctid,
      'extension': 'comp'
    };

    return new Promise((resolve, reject) => {
      this.apiService.getJsonData(uploadObj).subscribe((response: any) => {
        if (response) {
          this.firmReportsData = response ? JSON.parse(response) : [];
          this.calculateCountText();
        }
        resolve(JSON.stringify(this.firmReportsData));
      }, (error) => {
        this.firmReportsData = [];
        reject();
      });

    });
  }

  // Calculate count of sub folders and reports
  calculateCountText() {
    if (this.firmReportsData && this.firmReportsData.length) {
      this.firmReportsData.forEach(folder => {

        let folderReportCount = 0;

        // When folder has reports
        if (folder.reports && folder.reports.length) {
          folderReportCount = folder.reports.length;
        }

        // When folder has sub folders
        if (folder.subcategories && folder.subcategories.length) {
          let count = 0;

          folder.subcategories.forEach(subcat => {
            subcat['countText'] = subcat.reports.length == 1 ? '(1 report)' : '(' + subcat.reports.length + ' reports)';
            count += subcat.reports.length;
          });

          folderReportCount += count;

          folder['countText'] = folder.subcategories.length == 1 ? '(1 sub-folder' : '(' + folder.subcategories.length + ' sub-folders';
          folder['countText'] += ' & ' + (folderReportCount == 1 ? '1 report)' : folderReportCount + ' reports)');
        } else {
          // When folder has no sub folders
          if (folderReportCount != 0) {
            folder['countText'] = folderReportCount == 1 ? '(1 report)' : '(' + folderReportCount + ' reports)';
          }
        }

        //When folder has no reports
        if (folder.reports && folder.reports.length == 0 && folder.subcategories && folder.subcategories.length == 0) {
          folderReportCount = 0;
          folder['countText'] = '(' + folderReportCount + ' report)';
        }
      });
    }
  }

  // Checks if name is unique
  isUniqueReportTemplate(name: string, type: string, folderId?) {
    name = name.toLowerCase();
    let isUnique = true;

    if (type == 'report' && name && folderId) {
      // Check uniqueness of report name
      if (this.firmReportsData) {
        this.firmReportsData.forEach(folder => {
          if (folder.id == folderId) {
            if (folder.reports) {
              folder.reports.forEach(report => {
                if (report.name.toLowerCase() == name) {
                  isUnique = false;
                }
              });
            }

            if (folder.subcategories) {
              folder.subcategories.forEach(subcat => {
                if (subcat.reports) {
                  subcat.reports.forEach(report => {
                    if (report.name.toLowerCase() == name) {
                      isUnique = false;
                    }
                  });
                }
              });
            }
          } else {
            if (folder.subcategories) {
              folder.subcategories.forEach(subcat => {
                if (subcat.id == folderId) {
                  if (subcat.reports) {
                    subcat.reports.forEach(report => {
                      if (report.name.toLowerCase() == name) {
                        isUnique = false;
                      }
                    });
                  }
                }
              });
            }
          }
        });
      }
    } else if (type == 'subfolder' && name && folderId) {
      // Check uniqueness of folder name
      if (this.firmReportsData) {
        this.firmReportsData.forEach(folder => {
          if (folder.id == folderId) {
            if (folder.subcategories) {
              folder.subcategories.forEach(subcat => {
                if (subcat.name.toLowerCase() == name) {
                  isUnique = false;
                }
              });
            }
          }
        });
      }
    } else if (type == 'folder' && name) {
      // Check uniqueness of folder name
      if (this.firmReportsData) {
        this.firmReportsData.forEach(folder => {
          if (folder.name.toLowerCase() == name) {
            isUnique = false;
          }
        });
      }
    }

    return isUnique;
  }

  // Checks if the report name exists in the folder
  isReportNameInFolder(name, folder): boolean {
    let isNameExists = false;

    if (folder.reports) {
      folder.reports.forEach(report => {
        if (report.name.toLowerCase() == name) {
          isNameExists = true;
        }
      });
    }

    return isNameExists;
  }

  // Checks if multiple copies of the report exists in the folder
  isMultipleReportNamesInFolder(name, folder): number {
    let maxCopies = -1; // Starts with -1 to remove count for the original report

    if (folder.reports) {
      folder.reports.forEach(report => {
        if (report.name.substring(0, name.length).toLowerCase() == name && !isNaN(report.name.substring(name.length))) {
          if (maxCopies < +report.name.substring(name.length)) {
            maxCopies = +report.name.substring(name.length);
          }
        }
      });
    }

    return maxCopies + 1;
  }

  // Gets the new report name after checking for uniqueness
  getNewReportNameInFolder(name, folder): string {
    let newReportName = name;

    if (this.isReportNameInFolder(name.toLowerCase(), folder)) {
      let newCopyNumber = this.isMultipleReportNamesInFolder(name.toLowerCase(), folder);

      if (newCopyNumber > 0) {
        newReportName = newReportName + newCopyNumber;
      } else {
        newReportName = newReportName + '1';
      }
    }

    return newReportName;
  }

  // search templates (report & notes )
  getSearchedDetails(data, searchLibraryText, searchRelatedTo) {
    data.forEach(folder => {
      // Searching among names of categories
      if (searchLibraryText != '' && searchLibraryText.length > 2) {
        if (folder.name.toLowerCase().includes(searchLibraryText.toLowerCase())) {
          folder.isExpand = true;
        } else {
          folder.isExpand = false;
        }
      } else {
        folder.isExpand = false;
      }

      // Searching among notes directly inside the category
      if (searchRelatedTo == 'notestemplate') {
        if (folder.notes) {
          folder.notes.forEach(y => {
            if (searchLibraryText != '' && searchLibraryText.length > 2) {
              if (y.name.toLowerCase().includes(searchLibraryText.toLowerCase())) {
                folder.isExpand = true;
                y.isExpand = true;
              }
            } else {
              folder.isExpand = false;
              y.isExpand = false;
            }
          });
        }
      }
      if (searchRelatedTo == 'reporttemplate') {

        // Searches file names for report templates
        if (folder.reports) {
          folder.reports.forEach(report => {
            if (searchLibraryText != '' && searchLibraryText.length > 2) {
              if (report.name.toLowerCase().includes(searchLibraryText.toLowerCase())) {
                folder.isExpand = true;
                report.isExpand = true;
              }
            } else {
              folder.isExpand = false;
              report.isExpand = false;
            }
          });
        }
      }
      if (folder.subcategories) {
        folder.subcategories.forEach(subCategories => {
          // Searching among names of sub categories
          if (searchLibraryText != '' && searchLibraryText.length > 2) {
            if (subCategories.name.toLowerCase().includes(searchLibraryText.toLowerCase())) {
              folder.isExpand = true;
              subCategories.isExpand = true;
            }
          } else {
            folder.isExpand = false;
            subCategories.isExpand = false;
          }
          if (searchRelatedTo == 'notestemplate') {

            if (subCategories.notes) {
              subCategories.notes.forEach(subNotes => {
                // Searching among notes directly inside the sub category
                if (searchLibraryText != '' && searchLibraryText.length > 2) {
                  if (subNotes.name.toLowerCase().includes(searchLibraryText.toLowerCase())) {
                    folder.isExpand = true;
                    subCategories.isExpand = true;
                    subNotes.isExpand = true;
                  }
                } else {
                  folder.isExpand = false;
                  subCategories.isExpand = false;
                  subNotes.isExpand = false;
                }
              });
            } else {
              folder.isExpand = false;
              subCategories.isExpand = false;
            }
          }
          if (searchRelatedTo == 'reporttemplate') {

            // Searches file names for report templates
            if (subCategories.reports) {
              subCategories.reports.forEach(report => {
                // Searching among notes directly inside the sub category
                if (searchLibraryText != '' && searchLibraryText.length > 2) {
                  if (report.name.toLowerCase().includes(searchLibraryText.toLowerCase())) {
                    folder.isExpand = true;
                    subCategories.isExpand = true;
                    report.isExpand = true;
                  }
                } else {
                  folder.isExpand = false;
                  subCategories.isExpand = false;
                  report.isExpand = false;
                }
              });
            } else {
              folder.isExpand = false;
              subCategories.isExpand = false;
            }
          }
        });
      }
    });
    return data;
  }

  fetchReviewMiddleSection(moduleId: number | null | undefined, isMaster: number | null | undefined, searchIndex: string | null | undefined): Observable<string> {
    let params = new HttpParams();
    if (moduleId !== null && moduleId !== undefined) {
      params = params.set('moduleId', moduleId);
    }
    if (isMaster !== null && isMaster !== undefined) {
      params = params.set('isMaster', isMaster);
    }
    if (searchIndex) {
      params = params.set('searchIndex', searchIndex);
    }
    return this.http.get<GenericResModel<string>>(environment.apiV1 + '/review-middle-section', {params})
      .pipe(
        map(res => {
          if (res.status !== 200 && res.status !== 204) {
            res.errors.forEach(err => this.toaster.warning(err));
          }
          return res.data;
        })
      );
  }

  getWorksheetMasterFirmDetails(isMaster: number | null | undefined, search: string | null | undefined): Observable<string> {
    let params = new HttpParams();
    if (isMaster !== null && isMaster !== undefined) {
      params = params.set('isMaster', isMaster);
    }
    if (search) {
      params = params.set('search', search);
    }
    return this.http.get<GenericResModel<string>>(environment.apiV1 + '/worksheet-master-firm', {params})
      .pipe(
        map(res => {
          if (res.status !== 200 && res.status !== 204) {
            res.errors.forEach(err => this.toaster.warning(err));
          }
          return res.data;
        })
      );
  }
}
